import { useModal } from '@ebay/nice-modal-react';
import { ReactComponent as User } from '@shared/components/assets/icons/User.svg';
import Avatar from '@shared/components/Avatar/Avatar';
import DropdownButton from '@shared/components/DropdownButton/DropdownButton';
import { DropdownSelectionProps } from '@shared/components/DropdownButton/DropdownSelection';
import { meetingTestingConstants } from '@shared/constants';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as Logout } from 'assets/icons/Logout.svg';
import { ReactComponent as Settings } from 'assets/icons/Settings.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { CrossSigningStatusEnum } from 'contexts/MatrixDeviceContext/types';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import { FeatureFlag } from 'utils/Constants';
import testingConstants from 'utils/testingConstants';

import useLogout from './hooks/useLogout';
import useMxCookies from './hooks/useMxCookies';
import LogoutWarningModal from './LogoutWarningModal/LogoutWarningModal';
import AccountSettingsModal from './Settings/AccountSettings';
import UserSectionHeading from './UserSectionHeading';

/**
 * User dropdown component for header
 * Displays the user's avatar, display name, and a dropdown menu with options to copy the user ID, open the account settings, and logout
 */
const UserDropdown = () => {
  const { t } = useTranslation();
  const { matrixClient, mxAvatarUrl } = useMatrixContext();
  const logout = useLogout();
  const navigate = useNavigate();
  const { mxDisplayName, mxLoggedIn } = useMxCookies();
  const { show } = useModal(AccountSettingsModal);
  const logoutWarningModal = useModal(LogoutWarningModal);
  const { devicesVerificationStatus, hasSecurityCode, refreshHasSecurityCode, crossSigningStatus } =
    useMatrixDeviceContext();
  const isCrossSigningEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_CROSS_SIGNING);

  const handleLogout = async () => {
    if (!isCrossSigningEnabled) {
      logout(matrixClient);
      return;
    }

    // If the security code check hasn't resolved yet, fetch the fresh value before deciding
    const resolvedHasSecurityCode = hasSecurityCode === null ? await refreshHasSecurityCode() : hasSecurityCode;

    const hasRecovery =
      devicesVerificationStatus === CrossSigningStatusEnum.VERIFIED || resolvedHasSecurityCode === true;
    const shouldWarnAboutRecovery = crossSigningStatus === CrossSigningStatusEnum.VERIFIED && !hasRecovery;

    if (shouldWarnAboutRecovery) {
      logoutWarningModal.show({
        onLogout: () => logout(matrixClient),
        onPinCreated: refreshHasSecurityCode,
      });
    } else {
      logout(matrixClient);
    }
  };

  /**
   * Avatar icon with initials for example Alex Brown - AB, for the user dropdown
   * Displays the user's avatar if it exists, otherwise displays the default avatar
   */
  const avatarIcon = mxAvatarUrl ? (
    <img src={mxAvatarUrl} alt="avatar" className="shadow-sm object-cover shadow-gray-40 w-10 h-10 rounded-xl" />
  ) : (
    <Avatar size="header" name={mxDisplayName} className="cursor-pointer" />
  );

  const options: DropdownSelectionProps[] = [
    {
      title: t('settings'),
      icon: <Settings className="stroke-grayscale-black stroke-2 mr-2" />,
      className: 'text-grayscale-black',
      onClick: show,
      dataTestId: testingConstants.settingsOption,
    },
    ...(mxLoggedIn
      ? [
          {
            title: t('logout'),
            icon: <Logout className="stroke-2 stroke-alert-50 mr-3" data-testid={meetingTestingConstants.logoutBtn} />,
            className: 'text-alert-50',
            onClick: handleLogout,
            dataTestId: testingConstants.logoutOption,
          },
        ]
      : [
          {
            title: t('sign_in_register'),
            icon: <User className="stroke-2 stroke-primary-50 mr-2" />,
            className: 'text-primary-50',
            onClick: () => navigate('/auth'),
            dataTestId: testingConstants.signInOption,
          },
        ]),
  ];

  return (
    <DropdownButton
      options={options}
      optionClassName="text-alert-50"
      dataTestId={meetingTestingConstants.profileBtn}
      heading={<UserSectionHeading avatarIcon={avatarIcon} />}
    >
      {avatarIcon}
    </DropdownButton>
  );
};

export default UserDropdown;
